import axios, { AxiosError } from 'axios'

import { isAutomaticReauthenticationSuppressed } from '../../constants/auth'
import { UseAuthReturn } from '../../react'
import { initializeAuthAPI } from '../auth'
import { initializeEntityAPI } from '../entity'
import { initializeI18n } from '../i18n'
import { initializeMetadataAPI } from '../metadata'
import { ApiError } from './errors'

/**
 * The above type defines the options that can be passed to an API client, including authentication
 * token, headers, name, timeout, URI, and version.
 * @property {string} authToken - The `authToken` property is an optional string that represents an
 * authentication token that can be used to authenticate requests to an API. This token is typically
 * obtained by a user logging in to the API and is used to verify the user's identity and permissions
 * when making subsequent requests.
 * @property headers - The `headers` property is a record of key-value pairs representing the HTTP
 * headers to be sent with each API request. These headers can be used to provide additional
 * information to the server, such as authentication tokens or content types. The keys in the record
 * represent the header names, and the values represent the
 * @property {string} name - The name property is an optional string that represents the name of the
 * API client. It can be used for identification purposes or for logging.
 * @property {number} timeout - The `timeout` property is an optional number that represents the
 * maximum amount of time in milliseconds that the API client will wait for a response from the server
 * before timing out. If the server does not respond within the specified time, the client will throw
 * an error. If this property is not specified, the
 * @property {string} uri - The `uri` property is a string that represents the endpoint URL of the API
 * that the client will connect to. It can be an absolute URL or a relative URL.
 * @property {string} version - The version property is an optional string property that represents the
 * version of the API that the client is connecting to. It can be used to ensure compatibility between
 * the client and server versions.
 */
export type ApiClientOptions = {
  // typeDefs?: string | string[]; // api return types
  refToken?: string
  // jwtToken?: string // Commenting following JWT login due to deprecation in favor of Ref token
  headers?: Record<string, string>
  name?: string
  timeout?: number
  uri?: string
  version?: string
  auth?: UseAuthReturn
}

const defaultHeaders: Record<string, string> = {
  // headers are case insensitive (https://stackoverflow.com/a/5259004)
  accept: '*/*',
  // The content-type header describes the type of the body of the request, and
  // so it typically only is sent with requests that actually have bodies.
  'content-type': 'application/json',
  // IDP
  'X-Requested-With': 'XMLHttpRequest',
}
/**
 * The `ApiClient` class is a TypeScript class that represents an API client. It is responsible for
 * making HTTP requests to an API endpoint and handling the responses. The class has methods for
 * creating, reading, updating, and deleting resources on the API. It also has a constructor function
 * that sets up an Axios HTTP client with options such as the URI, authentication token, headers, and
 * timeout. The class uses the Axios library to make the actual HTTP requests.
 */
export class ApiClient {
  private readonly _httpClient

  private _auth?: UseAuthReturn

  private refToken?: string
  // private jwtToken?: string // Commenting following JWT login due to deprecation in favor of Ref token

  public readonly entity
  public readonly metadata
  public readonly i18n
  public readonly auth
  /**
   * This is a constructor function that sets up an Axios HTTP client with options such as URI,
   * authentication token, headers, and timeout.
   * @param {ApiClientOptions} options - An object containing optional properties
   */
  constructor(options: ApiClientOptions) {
    // Commenting following JWT login due to deprecation in favor of Ref token
    // const { uri = '/', refToken: refToken, jwtToken: jwtToken, headers, timeout = Number.MAX_SAFE_INTEGER } = options
    const { uri = '/', refToken, headers, timeout = Number.MAX_SAFE_INTEGER } = options

    this.refToken = refToken
    // this.jwtToken = jwtToken // Commenting following JWT login due to deprecation in favor of Ref token

    this._httpClient = axios.create({
      baseURL: uri,
    })
    // setup auth header
    if (refToken) {
      this._httpClient.defaults.headers.common['Authorization'] = `Bearer ${refToken}`
    }
    // add default headers
    for (const k in defaultHeaders) {
      this._httpClient.defaults.headers.common[k] = defaultHeaders[k]
    }
    //add custom headers
    for (const k in headers) {
      this._httpClient.defaults.headers.common[k] = headers[k]
    }
    // Override timeout default for the library
    // Now all requests using this instance will wait 2.5 seconds before timing out
    // this._httpClient.defaults.timeout = timeout
    // Add custom interceptor
    // this._httpClient.interceptors.request.use(function () {/*...*/}); //TODO get X-request-Id (traceparent id)

    this.entity = initializeEntityAPI(this)
    this.metadata = initializeMetadataAPI(this)
    this.i18n = initializeI18n(this)
    this.auth = initializeAuthAPI(this)
  }

  /**
   *
   * ------------------------------------------------------------------------
   * Commenting following JWT login due to deprecation in favor of Ref token
   * ------------------------------------------------------------------------
   *
   * This function sets the authorization token in the headers of an HTTP client.
   * @param {string} token - The `token` parameter is a string that represents an authentication token.
   * This token is used to authenticate the user and grant them access to protected resources on the
   * server. The `setToken` method sets this token in the HTTP client's headers so that it is sent with
   * every subsequent request made by the
   */
  // setToken(token: string) {
  //   this._httpClient.defaults.headers.common['Authorization'] = `Bearer ${token}`
  // }

  /**
   * ------------------------------------------------------------------------
   * Commenting following JWT login due to deprecation in favor of Ref token
   * ------------------------------------------------------------------------
   *
   * Allows update of the jwt token from the outside, this is necessary because the api client
   * must be initalized before the jwt token is avaialbe as the api client is used to make the call
   * to convert the refToken to a jwt. This is not good from a state management point of view as the
   * saved state should be immutable...
   */
  // updateJwtToken(token: string) {
  //   this.jwtToken = token
  // }
  //
  updateAuth(newAuth: UseAuthReturn | undefined) {
    if (newAuth) {
      this._auth = newAuth
    }
  }

  async checkForUnauthorized(e: AxiosError) {
    if (e.status === 401 && !isAutomaticReauthenticationSuppressed()) {
      await this._auth?.removeUser(false)
      await this._auth?.signIn()
    }
  }

  /**
   * Set the authorization header to use the ref token
   */
  setRefToken() {
    this._httpClient.defaults.headers.common['Authorization'] = `Bearer ${this.refToken}`
  }

  /**
   * ------------------------------------------------------------------------
   * Commenting following JWT login due to deprecation in favor of Ref token
   * ------------------------------------------------------------------------
   *
   * Set the authorization header to use the jwt token, this is necessary because to maintain
   * compatibility with external systems that have yet to update they must recieve a jwt token
   */
  // setJwtToken() {
  //   this._httpClient.defaults.headers.common['Authorization'] = `Bearer ${this.jwtToken}`
  // }

  /**
   * This is an asynchronous function that creates a resource by sending a PUT request to an endpoint
   * with a given body, and returns either the success response or an error.
   * @param {string} endpoint - The endpoint parameter is a string that represents the API endpoint where
   * the HTTP PUT request will be sent. It is used to specify the location of the resource that the
   * client wants to create or update.
   * @param {Body} body - The `body` parameter is of type `Body` and represents the data that will be
   * sent in the request body when making a PUT request to the specified `endpoint`. The type `Body` is a
   * generic type and can be any type that is expected by the API endpoint.
   * @returns The `create` function returns a Promise that resolves to either a `Success` object or an
   * `ApiError` object. If the HTTP request is successful, the function returns the `data` property of
   * the response object. If the request fails, the function returns a new `ApiError` object that wraps
   * the original error object thrown by Axios.
   */
  async create<Success, Body>(endpoint: string, body: Body): Promise<Success | ApiError> {
    try {
      const res = await this._httpClient.put<Success>(`/${endpoint}`, body)
      return res.data
    } catch (e) {
      await this.checkForUnauthorized(e as AxiosError)
      return new ApiError(e as AxiosError)
    }
  }

  /**
   * This is an asynchronous function that reads data from an API endpoint and returns either the data or
   * an error.
   * @param {string} endpoint - The endpoint is a string that represents the API endpoint that the
   * function will make a GET request to. It is the URL path that comes after the domain name. For
   * example, if the domain name is "https://example.com" and the endpoint is "/api/users", the full URL
   * would be "
   * @param [queryParams] - queryParams is an optional parameter of type Record<string, string> which
   * represents a set of key-value pairs that will be used as query parameters in the HTTP GET request.
   * These query parameters will be appended to the endpoint URL as a query string. If no query
   * parameters are provided, the URL will not have
   * @param [withCredentials] -indicates whether or not cross-site Access-Control requests should be made using credentials
   * @returns The `read` function returns a Promise that resolves to either a `Success` object or an
   * `ApiError` object. If the HTTP request is successful, the function returns the `data` property of
   * the response object. If the request fails, the function returns a new `ApiError` object that wraps
   * the error thrown by the Axios library.
   */
  async read<Success>(
    endpoint: string,
    queryParams?: Record<string, string> | URLSearchParams,
    withCredentials?: boolean,
    headers?: Record<string, string>,
  ): Promise<Success | ApiError> {
    try {
      const params = new URLSearchParams(queryParams)
      const res = await this._httpClient.get<Success>(`/${endpoint}`, { params, withCredentials, headers })
      return res.data
    } catch (e) {
      await this.checkForUnauthorized(e as AxiosError)
      return new ApiError(e as AxiosError)
    }
  }

  async readWithHeaders<Success>(
    endpoint: string,
    queryParams?: Record<string, string> | URLSearchParams,
    withCredentials?: boolean,
    headers?: Record<string, string>,
  ): Promise<{ data: Success; headers: Record<string, string> } | ApiError> {
    try {
      const params = new URLSearchParams(queryParams)
      const res = await this._httpClient.get<Success>(`/${endpoint}`, { params, withCredentials, headers })
      return { data: res.data, headers: res.headers as Record<string, string> }
    } catch (e) {
      await this.checkForUnauthorized(e as AxiosError)
      return new ApiError(e as AxiosError)
    }
  }

  /**
   * This is an asynchronous function that posts data from an API endpoint and returns either the data or
   * an error.
   * @param {string} endpoint - The endpoint is a string that represents the API endpoint that the
   * function will make a GET request to. It is the URL path that comes after the domain name. For
   * example, if the domain name is "https://example.com" and the endpoint is "/api/users", the full URL
   * would be "
   * @param {Body} body the post body
   * @param [queryParams] - queryParams is an optional parameter of type Record<string, string> which
   * represents a set of key-value pairs that will be used as query parameters in the HTTP GET request.
   * These query parameters will be appended to the endpoint URL as a query string. If no query
   * parameters are provided, the URL will not have
   * @param [withCredentials] -indicates whether or not cross-site Access-Control requests should be made using credentials
   * @returns The `read` function returns a Promise that resolves to either a `Success` object or an
   * `ApiError` object. If the HTTP request is successful, the function returns the `data` property of
   * the response object. If the request fails, the function returns a new `ApiError` object that wraps
   * the error thrown by the Axios library.
   */
  async post<Success, Body>(
    endpoint: string,
    body: Body,
    queryParams?: Record<string, string> | URLSearchParams,
    withCredentials?: boolean,
  ): Promise<Success | ApiError> {
    try {
      const params = new URLSearchParams(queryParams)
      const res = await this._httpClient.post<Success>(`/${endpoint}`, body, { params, withCredentials })
      return res.data
    } catch (e) {
      await this.checkForUnauthorized(e as AxiosError)
      return new ApiError(e as AxiosError)
    }
  }

  /**
   * This is an asynchronous function that updates data on an API endpoint and returns either the updated
   * data or an error.
   * @param {string} endpoint - The endpoint parameter is a string that represents the API endpoint that
   * the update method will send a POST request to.
   * @param {Body} body - The `body` parameter is the data that will be sent in the request body when
   * making an HTTP POST request to the specified `endpoint`. It can be of any type, depending on the
   * requirements of the API endpoint being called.
   * @returns The `update` function returns a Promise that resolves to either a `Success` object or an
   * `ApiError` object. If the HTTP request is successful, the function returns the `data` property of
   * the response object. If the request fails, the function returns a new `ApiError` object that wraps
   * the original error object thrown by Axios.
   */
  async update<Success, Body>(endpoint: string, body: Body): Promise<Success | ApiError> {
    try {
      const res = await this._httpClient.post<Success>(`/${endpoint}`, body)
      return res.data
    } catch (e) {
      await this.checkForUnauthorized(e as AxiosError)
      return new ApiError(e as AxiosError)
    }
  }

  /**
   * This is an asynchronous function that deleted data on an API endpoint and returns either a success
   * response or an error.
   * @param {string} endpoint - The endpoint parameter is a string that represents the API endpoint that
   * the delete method will send a DELETE request to.
   * @param {Body} body - The `body` parameter is the data that will be sent in the request body when
   * making an HTTP DELETE request to the specified `endpoint`. It can be of any type, depending on the
   * requirements of the API endpoint being called.
   * @returns The `delete` function returns a Promise that resolves to either a `Success` object or an
   * `ApiError` object. If the HTTP request is successful, the function returns the `data` property of
   * the response object. If the request fails, the function returns a new `ApiError` object that wraps
   * the original error object thrown by Axios.
   */
  async delete<Success, Body = unknown>(endpoint: string, body?: Body): Promise<Success | ApiError> {
    try {
      const config = body !== undefined ? { data: body } : undefined
      const res = await this._httpClient.delete<Success>(`/${endpoint}`, config)
      return res.data
    } catch (e) {
      await this.checkForUnauthorized(e as AxiosError)
      return new ApiError(e as AxiosError)
    }
  }

  /**
   * This is an asynchronous function that updates data on an API endpoint and returns either the updated
   * data or an error.
   * @param {string} endpoint - The endpoint parameter is a string that represents the API endpoint that
   * the update method will send a POST request to.
   * @param {Body} body - The `body` parameter is the data that will be sent in the request body when
   * making an HTTP POST request to the specified `endpoint`. It can be of any type, depending on the
   * requirements of the API endpoint being called.
   * @returns The `update` function returns a Promise that resolves to either a `Success` object or an
   * `ApiError` object. If the HTTP request is successful, the function returns the `data` property of
   * the response object. If the request fails, the function returns a new `ApiError` object that wraps
   * the original error object thrown by Axios.
   */
  async updateFormData<Success>(endpoint: string, body: FormData): Promise<Success | ApiError> {
    try {
      const res = await this._httpClient.post<Success>(`/${endpoint}`, body, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return res.data
    } catch (e) {
      await this.checkForUnauthorized(e as AxiosError)
      return new ApiError(e as AxiosError)
    }
  }

  /**
   * The function `readBlob` is an asynchronous function that reads a blob from a specified endpoint and
   * returns either the success response or an `ApiError` object.
   * @param {string} endpoint - The `endpoint` parameter is a string that represents the API endpoint or
   * URL that you want to send a request to. It specifies the location where the data is stored or the
   * action you want to perform on the server.
   * @param [queryParams] - queryParams is an optional parameter of type Record<string, string>. It
   * represents a set of key-value pairs that can be used as query parameters in the HTTP request. Each
   * key-value pair represents a query parameter, where the key is the parameter name and the value is
   * the parameter value.
   * @returns a Promise that resolves to either a `Success` object or an `ApiError` object.
   */
  async readBlob<Success>(endpoint: string, queryParams?: Record<string, string>): Promise<Success | ApiError> {
    try {
      const params = new URLSearchParams(queryParams)
      const res = await this._httpClient.get<Success>(`${endpoint}`, { params, responseType: 'blob' })
      return res.data
    } catch (e) {
      await this.checkForUnauthorized(e as AxiosError)
      return new ApiError(e as AxiosError)
    }
  }
  /**
   * The `downloadFile` function asynchronously downloads a file from a given endpoint and saves it with
   * a specified file name.
   * @param {string} endpoint - The `endpoint` parameter is a string that represents the URL or API
   * endpoint from which the file needs to be downloaded. It typically includes the necessary information
   * to identify the file or resource to be downloaded.
   * @param {string} fileName - The `fileName` parameter is a string that specifies the name of the file
   * to be downloaded.
   * @param [queryParams] - The `queryParams` parameter is an optional object that contains key-value
   * pairs representing the query parameters to be appended to the `endpoint` URL. These query parameters
   * are used to provide additional information or filters for the file download.
   */
  async downloadFile(endpoint: string, fileName: string, queryParams?: Record<string, string>): Promise<void> {
    const res = await this.readBlob<Blob>(endpoint, queryParams)
    if (res instanceof ApiError) {
      throw new Error(`💥 ${JSON.stringify(res)}`) // when needed
    } else {
      const url = window.URL.createObjectURL(res)
      const a = document.createElement('a')
      document.body.appendChild(a)
      a.setAttribute('style', 'display: none')
      a.href = url
      a.download = fileName
      a.click()
      window.URL.revokeObjectURL(url)
      a.remove()
    }
  }
}
